import { create } from "zustand";
import { createDemoWorkspace, type DemoWorkspaceData } from "@/lib/demoWorkspace";

export interface DemoRequestEvent {
  method: string;
  path: string;
  demo: true;
  ownerId: string;
  sequence: number;
}

interface DemoWorkspaceState {
  active: boolean;
  ownerId: string | null;
  revision: number;
  data: DemoWorkspaceData | null;
  lastRequest: DemoRequestEvent | null;
  enter: (userId: string) => void;
  exit: () => void;
  reset: () => void;
  mutate: (recipe: (data: DemoWorkspaceData) => void) => void;
  recordRequest: (method: string, path: string) => void;
}

// Session-only, account-keyed memory: no production cache, token or account
// setting is persisted here, and another account cannot see this dataset.
const sessions = new Map<string, { data: DemoWorkspaceData; revision: number }>();
let requestSequence = 0;
const ACTIVE_KEY = "trace.demo-workspace.active.v1";
const dataKey = (ownerId: string) => `trace.demo-workspace.data.v1.${ownerId}`;
function readSession(ownerId: string) {
  try {
    const raw = typeof sessionStorage !== "undefined" ? sessionStorage.getItem(dataKey(ownerId)) : null;
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ownerId?: string; data?: DemoWorkspaceData; revision?: number };
    if (parsed.ownerId !== ownerId || !parsed.data?.festivals?.length || !parsed.data.productionPlan || !parsed.data.riders?.length || !parsed.data.crewNeedsOverrides || !parsed.data.actIngestions || !parsed.data.preferredAirports) return null;
    return { data: parsed.data, revision: typeof parsed.revision === "number" ? parsed.revision : 0 };
  } catch { return null; }
}
function saveSession(ownerId: string, data: DemoWorkspaceData, revision: number) {
  sessions.set(ownerId, { data, revision });
  try { sessionStorage.setItem(dataKey(ownerId), JSON.stringify({ ownerId, data, revision })); } catch { /* Session memory still works when browser storage is unavailable. */ }
}
function storedOwner() {
  try { return typeof sessionStorage !== "undefined" ? sessionStorage.getItem(ACTIVE_KEY) : null; } catch { return null; }
}
const initialOwner = storedOwner();
const initialSession = initialOwner ? readSession(initialOwner) ?? { data: createDemoWorkspace(initialOwner), revision: 0 } : null;

export const useDemoWorkspaceStore = create<DemoWorkspaceState>((set, get) => ({
  // This is a pending demo session until auth verifies ownerId. The transport
  // rejects product requests for a null/different current user in the meantime.
  active: Boolean(initialOwner && initialSession),
  ownerId: initialSession ? initialOwner : null,
  revision: initialSession?.revision ?? 0,
  data: initialSession?.data ?? null,
  lastRequest: null,
  enter: (userId) => {
    if (!userId) throw new Error("Sign in before opening the demo workspace");
    const previous = sessions.get(userId) ?? readSession(userId) ?? { data: createDemoWorkspace(userId), revision: 0 };
    saveSession(userId, previous.data, previous.revision);
    try { sessionStorage.setItem(ACTIVE_KEY, userId); } catch { /* Optional tab-session storage. */ }
    set({ active: true, ownerId: userId, data: previous.data, revision: previous.revision, lastRequest: null });
  },
  exit: () => {
    try { sessionStorage.removeItem(ACTIVE_KEY); } catch { /* Optional tab-session storage. */ }
    set({ active: false, ownerId: null, data: null, revision: 0, lastRequest: null });
  },
  reset: () => {
    const ownerId = get().ownerId;
    if (!ownerId) return;
    const data = createDemoWorkspace(ownerId);
    saveSession(ownerId, data, 0);
    set({ data, revision: 0, lastRequest: null });
  },
  mutate: (recipe) => {
    const state = get();
    if (!state.active || !state.ownerId || !state.data) throw new Error("The demo workspace is closed");
    const data = structuredClone(state.data);
    recipe(data);
    const revision = state.revision + 1;
    saveSession(state.ownerId, data, revision);
    set({ data, revision });
  },
  recordRequest: (method, path) => {
    const ownerId = get().ownerId;
    if (!get().active || !ownerId) return;
    const event: DemoRequestEvent = { method, path, demo: true, ownerId, sequence: ++requestSequence };
    set({ lastRequest: event });
    if (typeof window !== "undefined") window.dispatchEvent(new CustomEvent("trace:tour-request", { detail: event }));
  },
}));
